import { GetServerSideProps, GetServerSidePropsContext, GetServerSidePropsResult, NextPage } from 'next'
import { useEffect } from 'react'
import getConfig from 'next/config'
import { ParsedUrlQuery } from 'node:querystring'
import getRawBody from 'raw-body'
import nookies from 'nookies'
import VehicleService, { VehicleDetailsDataObject } from '../services/vehicle-service'
import $console from '../../packages/utilities/src/$console'
import getSource from '../config/Sources'
import VrnService from '../services/vrn-service'
import { ApplicationFormAnswers } from '../components/ApplicationForm/ApplicationFormContext'
import Source from '../types/Source'
import { calculateFeatureConfig } from '../utils/features-utils/features-utils'
import { createQuestionConfig } from '../Questions/question-config-factory'
import { fireReactGAEvent, GAEventArgs } from '../components/Questions/utils'
import { isAutotraderAppCampaign, isAutotraderAppCampaignForAndroid } from '../utils/detect-device'
import { getPrefillDetails } from '../utils/getServerSideProps/getPrefillDetails'
import { extractExperimentAndVariant } from '../utils/experiment-utils/extractExperimentAndVariant'
import { buildV1Attribution, getParameter } from '../utils/utils-cookies'
import { handleFormlessJourneyServerSide } from '../utils/getServerSideProps/handleFormlessJourneyServerSide'
import { IndexProps } from '../types/IndexProps'
import { detectNavigationType, shouldBlockSubmissionForNavigationType } from '../utils/getServerSideProps/detectNavigationType'
import { renderApplicationForm } from '../utils/renderForm-utils/renderForm-utils'

const Index: NextPage<IndexProps> = ({
  questionConfig,
  vehicle,
  source,
  prefillData,
  applicationType,
  lenderDetails,
  showRepRate,
  eventsServiceUrl,
  customerId,
  experimentName,
  isBotTraffic,
  isOfficeTraffic,
  journey,
  featureSwitches,
  attribution,
  submissionResponse,
  mappedApplication,
  appFormEventParams
}: IndexProps): JSX.Element => {
  useEffect(() => {
    const appFormStartGAEvent: GAEventArgs = {
      category: 'Application Form',
      action: 'Start',
      label: applicationType,
      nonInteraction: true,
      prefillData: !!prefillData
    }

    if (prefillData) {
      appFormStartGAEvent.lenderName = lenderDetails?.lenderName || 'zuto'
    }

    fireReactGAEvent(appFormStartGAEvent)
  }, [])

  return renderApplicationForm({
    questionConfig,
    vehicle,
    source,
    prefillData,
    lenderDetails,
    showRepRate,
    eventsServiceUrl,
    customerId,
    experimentName,
    isBotTraffic,
    isOfficeTraffic,
    journey,
    featureSwitches,
    attribution,
    submissionResponse,
    mappedApplication,
    appFormEventParams
  })
}

const getVRN = (vrn?: string): string | undefined => {
  if (vrn) {
    if (vrn.replace(/\s+/g, '').length > 7) {
      return VrnService.decrypt(vrn)
    }
    return vrn
  }
  return undefined
}

const getVehicleDetailsFromURL = (query: ParsedUrlQuery): VehicleDetailsDataObject | undefined => {
  const vehiclePrice = query.vehiclePrice || query.value
  const advertUrl = query.CarUrl
  if (vehiclePrice) {
    const vehicle: VehicleDetailsDataObject = {
      price: vehiclePrice ? parseInt(vehiclePrice as string, 10) : undefined,
      make: query.make ? (query.make as string) : undefined,
      model: query.model ? (query.model as string) : undefined,
      mileage: query.mileage ? parseInt(query.mileage as string, 10) : undefined,
      registration: getVRN(query.vrn as string),
      imageUrls: query.imageurl
        ? [
            query.imageurl as string
            // replace undefined with placeholder image
          ]
        : undefined,
      dealerName: query.dealer ? (query.dealer as string) : undefined,
      registrationYear: query.regyear ? parseInt((query.regyear as string).substring(0, 4), 10) : undefined,
      type: query.vrn ? 'CAR' : undefined
    }
    if (advertUrl) {
      vehicle.advertUrl = Array.isArray(advertUrl) ? advertUrl[0] : advertUrl
    }
    return vehicle
  }

  return undefined
}

// TODO: Add tests and then split this into multiple functions.
export const getServerSideProps: GetServerSideProps = async (context: GetServerSidePropsContext): Promise<GetServerSidePropsResult<IndexProps>> => {
  const navigationType = detectNavigationType(context.req)

  const config = {
    eventsApi: process.env.eventsApi,
    zutoAnalyticsUrl: process.env.zutoAnalyticsUrl
  }

  const bodyParams: Record<string, string> = {}
  if (context.req.method === 'POST') {
    const body = (await getRawBody(context.req)).toString('utf-8')
    const pairs = body.split('&')
    pairs.forEach((p) => {
      const [key, value] = p.split('=')
      bodyParams[key] = value
    })
  }

  const {
    zuto_quoteid: zutoQuoteIdCookie,
    zuto_customerid: zutoCustomerIdCookie,
    zuto_advertid: zutoAdvertIdCookie,
    zuto_showRepRate: zutoShowRepRateCookie,
    zuto_wbraid: wbraid,
    zuto_gbraid: gbraid,
    zuto_fbclid: fbclid
  } = nookies.get(context)

  const { serverRuntimeConfig } = getConfig()
  const queryAdvertId = context.query.advertId || context.query.advertid
  const advertId = getParameter(queryAdvertId, zutoAdvertIdCookie)

  const deviceId = context.req.headers.device as string
  const sessionId = context.req.headers.session as string
  const isOfficeTraffic = (context.req.headers['z-is-office-traffic'] as string) === '1'
  const isBotTraffic = (context.req.headers['z-is-bot-traffic'] as string) === '1'
  const journey = (context.req.headers['z-journey'] as string) || 'AppForm2021'
  const attribution = buildV1Attribution(context)
  const showRepRate = getParameter(context.query.showRepRate, zutoShowRepRateCookie)

  let customerId = bodyParams?.customerId || getParameter(context.query.customerId, zutoCustomerIdCookie, true)
  let quoteId = bodyParams?.quoteId || getParameter(context.query.quoteId, zutoQuoteIdCookie)

  // MSM bundle the customerId and quoteId inside one query param
  if (customerId?.includes('&')) {
    const [customerIdValue, quoteIdParam] = customerId.split('&')
    customerId = customerIdValue
    const [, quoteIdValue] = quoteIdParam.split('=')
    quoteId = quoteIdValue
  }

  let vehicle
  let source: Source | undefined

  const prefillDetailsResult = await getPrefillDetails({
    quoteId,
    customerId,
    attribution,
    deviceId
  })

  let applicationType = prefillDetailsResult.applicationType
  const lenderDetails = prefillDetailsResult.lenderDetails
  const prefillData: ApplicationFormAnswers | undefined = prefillDetailsResult.prefillData

  // If we have the AdvertID and a Source, Use the API
  const isClassifiedSource = attribution.source === 'autotrader'

  if (advertId && attribution.source && isClassifiedSource) {
    const vehicleService = VehicleService({
      vehicleURL: serverRuntimeConfig.vehicleURL
    })
    try {
      vehicle = await vehicleService.getVehicle(advertId, attribution.source)
    } catch (e) {
      $console.error(e)
      $console.error('context', context)
    }
  } else {
    // Otherwise, get the details from the URL params provided
    vehicle = getVehicleDetailsFromURL(context.query)
  }

  if (attribution.source) {
    source = getSource(attribution.source)
  }

  if (vehicle) {
    applicationType = 'vehicle advert'
  }

  const featureConfig = await calculateFeatureConfig({
    attribution,
    deviceId,
    lenderDetails,
    prefillData: prefillData,
    comesFromAutotraderApp: isAutotraderAppCampaign(context.query),
    comesFromAutotraderAppAndroidOnly: isAutotraderAppCampaignForAndroid(context.query)
  })

  const questionConfig = createQuestionConfig({
    features: featureConfig.featuresV1,
    lenderDetails,
    vehicle
  })

  $console.info(
    JSON.stringify({
      eventType: 'ApplyPageServed',
      source: attribution.source,
      campaign: attribution.campaign,
      lender: lenderDetails ? `${lenderDetails.lenderName} - ${lenderDetails?.preapproved}` : 'none',
      questionSet: questionConfig,
      navigationType
    })
  )

  const { experimentName, variant } = extractExperimentAndVariant(featureConfig.experimentName)

  context.res.setHeader(
    'Z-Experiments',
    JSON.stringify([
      {
        ExperimentName: experimentName,
        Variant: variant
      }
    ])
  )
  
  const isOnAFormlessJourney = Object.entries(featureConfig.featuresV1)
  .some(([key, value]) => /^formlessJourneyV\d+$/.test(key) && value)
  
  // True if this request was triggered by a real user action (not speculative/prefetch etc)
  const isRealUserNavigation = !shouldBlockSubmissionForNavigationType(navigationType)

  if (isOnAFormlessJourney && isRealUserNavigation && prefillData) {
    const { appFormEventParams, mappedApplication, submissionResponse } = await handleFormlessJourneyServerSide({
      context,
      eventsApi: config.eventsApi,
      navigationType,
      isBotTraffic,
      isOfficeTraffic,
      journey,
      deviceId,
      sessionId,
      attribution,
      featureConfig,
      lenderDetails,
      prefillData,
      customerId,
      wbraid,
      gbraid,
      fbclid
    })

    // props
    const typedProps: IndexProps = {
      questionConfig,
      vehicle,
      source,
      prefillData,
      applicationType,
      lenderDetails,
      showRepRate: showRepRate === 'true' ? true : source?.showRepresentativeExample,
      eventsServiceUrl: config.eventsApi,
      customerId,
      experimentName: featureConfig.experimentName,
      attribution,
      isOfficeTraffic,
      isBotTraffic,
      journey,
      featureSwitches: featureConfig.featuresV1,
      submissionResponse,
      mappedApplication,
      appFormEventParams
    }

    return {
      props: JSON.parse(JSON.stringify(typedProps))
    }
  }

  const typedProps: IndexProps = {
    questionConfig,
    vehicle,
    source,
    prefillData,
    applicationType,
    lenderDetails,
    showRepRate: showRepRate === 'true' ? true : source?.showRepresentativeExample,
    eventsServiceUrl: config.eventsApi,
    customerId,
    experimentName: featureConfig.experimentName,
    attribution,
    isOfficeTraffic,
    isBotTraffic,
    journey,
    featureSwitches: featureConfig.featuresV1
  }

  return {
    props: JSON.parse(JSON.stringify(typedProps))
  }
}

export default Index